import React, { useState, useEffect, useCallback } from "react";
import { FaListAlt, FaSearch, FaCheckCircle, FaTimesCircle, FaUndo,
         FaChevronDown, FaCreditCard, FaMoneyBillWave,
         FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import {useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import { useDateTimeFormat } from "./utils/formatters";
import { PrimaryButton, GhostButton, DangerButton } from "./components/ThemedButton";
import StatusBadge from "./components/StatusBadge";

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

const STATUS_META = {
  pending_payment: { label: "Pending",    tone: "warning" },
  confirmed:       { label: "Confirmed",  tone: "success" },
  checked_in:      { label: "Checked In", tone: "info" },
  completed:       { label: "Completed",  tone: "info" },
  no_show:         { label: "No Show",    tone: "warning" },
  cancelled:       { label: "Cancelled",  tone: "neutral" },
};

// ── Refund Modal ───────────────────────────────────────────────────────────────
function RefundModal({ booking, T, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState("");

  const maxRefund = parseFloat(booking.total_amount) - parseFloat(booking.total_refunded || 0);

  const submit = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0 || val > maxRefund) {
      setErr(`Amount must be between $0.01 and $${maxRefund.toFixed(2)}`); return;
    }
    setBusy(true); setErr("");
    const res = await fetch(`${BASE}/lb/bookings/${booking.booking_id}/refund`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount: val, reason, refunded_by: "admin" }),
    });
    if (res.ok) { onDone(val); }
    else { const d = await res.json(); setErr(d.detail || "Refund failed"); }
    setBusy(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 600, background: "rgba(0,0,0,0.5)",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: T.glass, backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
                    border: T.glassBdr, borderRadius: 16, padding: "28px 32px",
                    width: 420, boxShadow: T.glassSh }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: FONT, marginBottom: 6 }}>
          Issue Refund
        </div>
        <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginBottom: 20 }}>
          {booking.booking_ref} · Paid: ${parseFloat(booking.total_amount).toFixed(2)} ·
          Already refunded: ${parseFloat(booking.total_refunded || 0).toFixed(2)}
        </div>
        <label style={{ fontSize: 12, fontWeight: 700, color: T.textLabel, fontFamily: FONT }}>
          Refund Amount (max ${maxRefund.toFixed(2)})
        </label>
        <input type="number" step="0.01" min="0.01" max={maxRefund}
          value={amount} onChange={e => setAmount(e.target.value)}
          placeholder={`Up to $${maxRefund.toFixed(2)}`}
          style={{ display: "block", width: "100%", marginTop: 6, marginBottom: 14,
                   padding: "9px 12px", borderRadius: 8, border: `1px solid ${T.inputBdr}`,
                   background: T.inputBg, color: T.inputText, fontSize: 14,
                   fontFamily: FONT, outline: "none", boxSizing: "border-box" }} />
        <label style={{ fontSize: 12, fontWeight: 700, color: T.textLabel, fontFamily: FONT }}>
          Reason (optional)
        </label>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
          placeholder="Customer requested cancellation…"
          style={{ display: "block", width: "100%", marginTop: 6, marginBottom: 14, resize: "vertical",
                   padding: "9px 12px", borderRadius: 8, border: `1px solid ${T.inputBdr}`,
                   background: T.inputBg, color: T.inputText, fontSize: 13,
                   fontFamily: FONT, outline: "none", boxSizing: "border-box" }} />
        {err && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 12, fontFamily: FONT }}>{err}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <GhostButton onClick={onClose} style={{ fontFamily: FONT }}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={submit} disabled={busy} style={{ fontFamily: FONT }}>
            {busy ? "Processing…" : "Issue Refund"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ── Booking Row ────────────────────────────────────────────────────────────────
function BookingRow({ b, T, onAction, onRefund, busy, forceExpand }) {
  const [expanded, setExpanded] = useState(false);
  const { fmtDate, fmtTime } = useDateTimeFormat();

  // Sync with global expand-all toggle
  useEffect(() => {
    if (forceExpand !== undefined) setExpanded(forceExpand);
  }, [forceExpand]);
  const SMMeta = STATUS_META[b.status] || { label: b.status, tone: "neutral" };

  // Neutral actions use the shared GhostButton (matches POS/OTO); Cancel is
  // the one genuinely destructive action here, so it's the only DangerButton
  // — same "ghost for regular actions, danger only for destructive ones"
  // convention used everywhere else, instead of a bespoke color per action.
  const btn = (Danger, Icon, label, onClick) => {
    const Btn = Danger ? DangerButton : GhostButton;
    return (
      <Btn onClick={onClick} disabled={!!busy} title={label} size="sm" style={{ fontFamily: FONT }}>
        <Icon size={10} />{busy ? "…" : label}
      </Btn>
    );
  };

  const paymentLabel = b.stripe_payment_status === "succeeded"
    ? "Paid via Stripe"
    : b.stripe_payment_status === "failed"
    ? "Card charge failed"
    : b.stripe_payment_status === "offline"
    ? "Paid offline (cash / EFTPOS)"
    : b.stripe_payment_intent_id
    ? `Stripe: ${b.stripe_payment_status || "pending"}`
    : b.is_academy_booking
    ? "Academy (no charge)"
    : b.status === "pending_payment"
    ? "Awaiting payment"
    : "Not recorded";

  const cardBrand = (b.card_brand || "Card").replace(/^\w/, c => c.toUpperCase());
  const cardExp   = b.card_exp_month
    ? ` exp ${b.card_exp_month}/${String(b.card_exp_year || "").slice(-2)}` : "";
  const hasCard   = !!b.card_last4;
  const cardLabel = hasCard
    ? `${cardBrand} ••••${b.card_last4}${cardExp}`
    : b.stripe_pm_id ? "Card registered (last 4 not synced)" : null;

  const bookedBy = b.booked_by_type
    ? b.booked_by_type.charAt(0).toUpperCase() + b.booked_by_type.slice(1) : "—";
  const hasPI = !!b.stripe_payment_intent_id;

  const fields = [
    ["Booking Type", b.type_name || "—"],
    ["Duration",     b.duration_minutes >= 60
      ? `${(b.duration_minutes/60).toFixed(b.duration_minutes%60===0?0:1)} hr${b.duration_minutes!==60?"s":""}`
      : `${b.duration_minutes} min`],
    ["Member Rate",  b.is_member_rate ? "Yes" : "No"],
    ["Academy",      b.is_academy_booking ? "Yes" : "No"],
    ["Booked By",    bookedBy],
    ["Payment",      paymentLabel],
    hasPI && ["Stripe PI", `…${b.stripe_payment_intent_id.slice(-12)}`],
    ["Notes",        b.notes || "—"],
  ].filter(Boolean);

  const isHrsBooking  = parseInt(b.is_member_rate) === 1 && parseFloat(b.total_amount || 0) === 0;
  const isChargePortn = b.split_role === "charge_portion";
  const isHrsPortion  = b.split_role === "hours_portion";
  const isSplit       = !!b.split_group_id;
  const hrsRefunded   = parseFloat(b.hours_refunded || 0);
  const dolRefunded   = parseFloat(b.total_refunded || 0);
  const durHrs        = parseFloat(b.duration_minutes || 0) / 60;

  const durLabel = b.duration_minutes >= 60
    ? `${durHrs.toFixed(b.duration_minutes % 60 === 0 ? 0 : 1)} hr${b.duration_minutes !== 60 ? "s" : ""}`
    : `${b.duration_minutes} min`;

  return (
    <>
      <tr style={{ borderBottom: `1px solid ${T.divider}`, opacity: busy ? 0.7 : 1,
                   transition: "opacity 0.2s",
                   background: isHrsPortion ? "rgba(99,102,241,0.03)" : isChargePortn ? "rgba(245,158,11,0.03)" : "transparent" }}>
        {/* Ref */}
        <td style={{ padding: "12px 14px", fontSize: 12, fontWeight: 700, color: "#3b82f6",
                     fontFamily: FONT, whiteSpace: "nowrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            {isSplit && <span title={isHrsPortion ? "Split — hours portion" : "Split — charge portion"}
              style={{ fontSize: 10, background: isHrsPortion ? "rgba(99,102,241,0.15)" : "rgba(245,158,11,0.15)",
                       color: isHrsPortion ? "#6366f1" : "#f59e0b", borderRadius: 4,
                       padding: "1px 5px", fontWeight: 800 }}>
              {isHrsPortion ? "⚡ Hrs" : "⚡ $"}
            </span>}
            {b.booking_ref}
          </div>
          {isHrsPortion && <div style={{ fontSize: 10, color: "#6366f1", marginTop: 2 }}>Hrs pool</div>}
          {isChargePortn && <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 2 }}>Overage charge</div>}
        </td>
        {/* Client */}
        <td style={{ padding: "12px 14px", fontSize: 12, color: T.text, fontFamily: FONT, minWidth: 160 }}>
          <div style={{ fontWeight: 600 }}>{b.client_name}</div>
          {b.client_email && <div style={{ fontSize: 11, color: T.textMuted }}>{b.client_email}</div>}
        </td>
        {/* Lane */}
        <td style={{ padding: "12px 14px", fontSize: 12, fontWeight: 600, color: T.text,
                     fontFamily: FONT, whiteSpace: "nowrap" }}>{b.lane_name}</td>
        {/* Date & Time */}
        <td style={{ padding: "12px 14px", fontSize: 12, color: T.text, fontFamily: FONT, whiteSpace: "nowrap" }}>
          <div style={{ fontWeight: 600 }}>{fmtDate(b.booking_date)}</div>
          <div style={{ fontSize: 11, color: T.textMuted }}>{fmtTime(b.start_time)} – {fmtTime(b.end_time)}</div>
        </td>
        {/* Duration */}
        <td style={{ padding: "12px 14px", fontSize: 12, color: T.text, fontFamily: FONT, whiteSpace: "nowrap" }}>
          {isHrsBooking
            ? <span style={{ color: "#6366f1", fontWeight: 700 }}>{durLabel} <span style={{ fontSize: 10, opacity: 0.7 }}>pool</span></span>
            : durLabel}
        </td>
        {/* Amount */}
        <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 700, color: T.text,
                     fontFamily: FONT, whiteSpace: "nowrap" }}>
          {isHrsBooking
            ? <span style={{ fontSize: 11, color: "#6366f1" }}>Hrs pool</span>
            : `$${parseFloat(b.total_amount).toFixed(2)}`}
        </td>
        {/* Refunded */}
        <td style={{ padding: "12px 14px", fontSize: 12, fontFamily: FONT, whiteSpace: "nowrap" }}>
          {hrsRefunded > 0 ? (
            <span style={{ fontWeight: 700, color: "#6366f1" }}>
              –{hrsRefunded.toFixed(1)} hrs
              <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 400 }}>returned to pool</div>
            </span>
          ) : dolRefunded > 0 ? (
            <span style={{ fontWeight: 700, color: "#ef4444" }}>
              –${dolRefunded.toFixed(2)}
            </span>
          ) : (
            <span style={{ color: T.textMuted }}>—</span>
          )}
        </td>
        {/* Status */}
        <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
          <StatusBadge label={SMMeta.label} tone={SMMeta.tone} style={{ fontFamily: FONT }} />
        </td>
        {/* Actions */}
        <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
            {b.status === "pending_payment" && b.stripe_pm_id &&
              btn(false, FaCreditCard, "Charge Card", () => onAction(b.booking_id, "charge"))}
            {b.status === "pending_payment" &&
              btn(false, FaMoneyBillWave, "Mark Paid", () => onAction(b.booking_id, "mark-paid"))}
            {b.status === "confirmed" && b.booking_date === new Date().toISOString().slice(0, 10) &&
              btn(false, FaCheckCircle, "Check In", () => onAction(b.booking_id, "checkin"))}
            {!["cancelled", "completed"].includes(b.status) &&
              btn(true, FaTimesCircle, "Cancel", () => onAction(b.booking_id, "cancel"))}
            {["confirmed", "checked_in", "completed"].includes(b.status) &&
              parseFloat(b.total_amount) > parseFloat(b.total_refunded || 0) &&
              btn(false, FaUndo, "Refund", () => onRefund(b))}
          </div>
        </td>
        <td style={{ padding: "12px 10px", whiteSpace: "nowrap" }}>
          <button onClick={() => setExpanded(x => !x)} title="Details" style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 28, height: 28, borderRadius: 7, cursor: "pointer",
            background: T.tabInBg, border: `1px solid ${T.glassBdr}`,
            color: T.textMuted, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
          }}>
            <FaChevronDown size={9} style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "0.2s" }} />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: T.inputBg }}>
          <td colSpan={10} style={{ padding: "14px 24px 16px" }}>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap", fontSize: 12,
                          fontFamily: FONT, alignItems: "flex-start" }}>
              {fields.map(([k, v]) => (
                <div key={k} style={{ minWidth: 100 }}>
                  <div style={{ color: T.textLabel, marginBottom: 3, fontSize: 10, fontWeight: 800,
                                textTransform: "uppercase", letterSpacing: "0.07em" }}>{k}</div>
                  <div style={{ color: T.text, fontWeight: 600 }}>{v}</div>
                </div>
              ))}
              <div style={{ minWidth: 140 }}>
                <div style={{ color: T.textLabel, marginBottom: 3, fontSize: 10, fontWeight: 800,
                              textTransform: "uppercase", letterSpacing: "0.07em" }}>Card on File</div>
                {cardLabel
                  ? <div style={{ color: hasCard ? T.text : "#f59e0b", fontWeight: 600 }}>{cardLabel}</div>
                  : <div style={{ color: "#ef4444", fontWeight: 600, fontSize: 11 }}>
                      Not saved —{" "}
                      <span style={{ textDecoration: "underline", cursor: "pointer", color: "#3b82f6" }}
                        onClick={() => alert("Go to Client Manager → select client → Add Card")}>
                        add in Client Manager
                      </span>
                    </div>
                }
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Sort helpers ───────────────────────────────────────────────────────────────
const SORT_COLS = {
  ref:    (a, b) => a.booking_ref.localeCompare(b.booking_ref),
  client: (a, b) => (a.client_name || "").localeCompare(b.client_name || ""),
  lane:   (a, b) => (a.lane_name  || "").localeCompare(b.lane_name  || ""),
  date:   (a, b) => {
    const da = a.booking_date + "T" + (a.start_time || "");
    const db = b.booking_date + "T" + (b.start_time || "");
    return da.localeCompare(db);
  },
  amount: (a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount),
  status: (a, b) => (a.status || "").localeCompare(b.status || ""),
  duration: (a, b) => (a.duration_minutes || 0) - (b.duration_minutes || 0),
  refunded: (a, b) => parseFloat(a.total_refunded || 0) - parseFloat(b.total_refunded || 0),
};

function SortTh({ label, col, sort, onSort, style }) {
  const active = sort.col === col;
  const Icon = active ? (sort.dir === "asc" ? FaSortUp : FaSortDown) : FaSort;
  return (
    <th onClick={() => onSort(col)}
      style={{ padding: "12px 14px", fontSize: 11, fontWeight: 800,
               cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
               textAlign: "left", ...style }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        {label}
        <Icon size={10} style={{ opacity: active ? 1 : 0.35 }} />
      </span>
    </th>
  );
}

// ── Main Export ────────────────────────────────────────────────────────────────
export default function LaneBookingAdmin() {
  const { isDark } = useTheme();
  const T = getT(isDark);

  const [facilities,   setFacilities]   = useState([]);
  const [bookings,     setBookings]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [busyIds,      setBusyIds]      = useState({});   // {booking_id: true}
  const [allExpanded,  setAllExpanded]  = useState(false);
  const [filters,      setFilters]      = useState({
    facility_id: "", status: "", search: "", month: "", year: new Date().getFullYear(),
  });
  const [sort,         setSort]         = useState({ col: "date", dir: "desc" });
  const [refundTarget, setRefundTarget] = useState(null);
  const [page,         setPage]         = useState(1);
  const PER_PAGE = 20;

  useEffect(() => {
    fetch(`${BASE}/lb/facilities`).then(r => r.json())
      .then(d => setFacilities(d.facilities || []));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams({ limit: 200 });
    if (filters.facility_id) q.set("facility_id", filters.facility_id);
    if (filters.status)      q.set("status",      filters.status);
    if (filters.month)       q.set("month",        filters.month);
    if (filters.year)        q.set("year",         filters.year);
    fetch(`${BASE}/lb/bookings?${q}`).then(r => r.json())
      .then(d => { setBookings(d.bookings || []); setLoading(false); setPage(1); })
      .catch(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Optimistic update: change only the affected row in state; sync with server in background
  const patchBooking = useCallback((id, patch) => {
    setBookings(prev => prev.map(b =>
      b.booking_id === id ? { ...b, ...patch } : b
    ));
  }, []);

  const doAction = async (id, endpoint) => {
    setBusyIds(prev => ({ ...prev, [id]: true }));

    // Optimistic status update so the row updates immediately
    const optimisticStatus = {
      charge:      "confirmed",
      "mark-paid": "confirmed",
      checkin:     "checked_in",
      cancel:      "cancelled",
    }[endpoint];
    if (optimisticStatus) patchBooking(id, { status: optimisticStatus });

    const body = endpoint === "cancel"
      ? { reason: "Admin cancelled", cancelled_by: "admin", issue_refund: true }
      : {};

    try {
      const res = await fetch(`${BASE}/lb/bookings/${id}/${endpoint}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        // Fetch the single updated booking to sync actual server state
        const updated = await fetch(`${BASE}/lb/bookings/${id}`).then(r => r.json()).catch(() => null);
        if (updated?.booking) patchBooking(id, updated.booking);
      } else {
        // Revert optimistic update on failure
        const d = await res.json().catch(() => ({}));
        // card_invalid: PM permanently tainted — show specific guidance
        let errDetail = d.detail || `Action '${endpoint}' failed`;
        try {
          const parsed = typeof errDetail === "string" ? JSON.parse(errDetail) : errDetail;
          if (parsed?.error === "card_invalid") {
            errDetail = `⚠️ Card No Longer Valid\n\n${parsed.message}\n\nNext steps:\n1. Ask the customer to re-register their payment method via the booking portal.\n2. Then retry the charge from Booking Management.`;
          }
        } catch {}
        alert(errDetail);
        load(); // full reload to restore correct state
      }
    } catch {
      alert("Network error — please refresh");
      load();
    } finally {
      setBusyIds(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  const handleRefundDone = (bookingId, refundedAmount) => {
    // Optimistic: update total_refunded and check if fully refunded → cancel
    setBookings(prev => prev.map(b => {
      if (b.booking_id !== bookingId) return b;
      const newRefunded = parseFloat(b.total_refunded || 0) + refundedAmount;
      const isFull = newRefunded >= parseFloat(b.total_amount);
      return { ...b, total_refunded: newRefunded, status: isFull ? "cancelled" : b.status,
               refund_status: isFull ? "fully_refunded" : "partially_refunded" };
    }));
    setRefundTarget(null);
    // Background sync for accuracy
    setTimeout(() => {
      fetch(`${BASE}/lb/bookings/${bookingId}`).then(r => r.json())
        .then(d => { if (d.booking) patchBooking(bookingId, d.booking); })
        .catch(() => {});
    }, 800);
  };

  const onSort = (col) => {
    setSort(prev => prev.col === col
      ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { col, dir: "asc" }
    );
  };

  const filtered = bookings.filter(b => {
    if (!filters.search) return true;
    const s = filters.search.toLowerCase();
    return (b.booking_ref?.toLowerCase().includes(s) ||
            b.client_name?.toLowerCase().includes(s) ||
            b.client_email?.toLowerCase().includes(s));
  });

  const sorted = [...filtered].sort((a, b) => {
    const cmp = SORT_COLS[sort.col] ? SORT_COLS[sort.col](a, b) : 0;
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const pages     = Math.ceil(sorted.length / PER_PAGE);
  const paginated = sorted.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const card = { background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
                 border: T.glassBdr, borderRadius: 16, boxShadow: T.glassSh };

  const sel = (key, opts) => (
    <select value={filters[key]} onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
      style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.inputBdr}`,
               background: T.inputBg, color: T.inputText, fontSize: 13, fontFamily: FONT,
               outline: "none", minWidth: 130 }}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const thStyle = { color: T.textLabel, borderBottom: `2px solid ${T.divider}`, fontFamily: FONT };

  return (
    <div style={{ padding: "28px 32px", maxWidth: "100%" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <PageHeaderIcon icon={<FaListAlt size={20} color={getBrandColors().blueDark} />} />
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: FONT }}>Booking Management</div>
          <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
            {filtered.length} booking{filtered.length !== 1 ? "s" : ""} found
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ ...card, padding: "16px 20px", marginBottom: 18, display: "flex",
                    gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: "1 1 200px" }}>
          <FaSearch size={12} style={{ position: "absolute", left: 10, top: "50%",
                                       transform: "translateY(-50%)", color: T.textMuted }} />
          <input value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            placeholder="Ref, client name or email…"
            style={{ padding: "8px 12px 8px 30px", borderRadius: 8, border: `1px solid ${T.inputBdr}`,
                     background: T.inputBg, color: T.inputText, fontSize: 13,
                     fontFamily: FONT, outline: "none", width: "100%", boxSizing: "border-box" }} />
        </div>
        {sel("facility_id", [["", "All Facilities"], ...facilities.map(f => [f.facility_id, f.facility_name])])}
        {sel("status", [
          ["", "All Statuses"],
          ["pending_payment", "Pending"], ["confirmed", "Confirmed"],
          ["checked_in", "Checked In"], ["completed", "Completed"],
          ["cancelled", "Cancelled"],   ["no_show", "No Show"],
        ])}
        {sel("month", [["", "All Months"], ...MONTHS.map((m, i) => [i + 1, m])])}
        {sel("year", [[new Date().getFullYear(), "This Year"],
                      [new Date().getFullYear() - 1, "Last Year"]])}
        <PrimaryButton onClick={load}
          style={{ borderRadius: 8, fontWeight: 800, fontFamily: FONT }}>
          Refresh
        </PrimaryButton>
      </div>

      {/* Table */}
      <div style={{ ...card, overflowX: "auto" }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: T.textMuted, fontFamily: FONT }}>Loading…</div>
        ) : (
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <SortTh label="Ref"        col="ref"    sort={sort} onSort={onSort} style={thStyle} />
                <SortTh label="Client"     col="client" sort={sort} onSort={onSort} style={thStyle} />
                <SortTh label="Lane"       col="lane"   sort={sort} onSort={onSort} style={thStyle} />
                <SortTh label="Date & Time" col="date"  sort={sort} onSort={onSort} style={thStyle} />
                <SortTh label="Duration"   col="duration" sort={sort} onSort={onSort} style={thStyle} />
                <SortTh label="Amount"     col="amount" sort={sort} onSort={onSort} style={thStyle} />
                <SortTh label="Refunded"   col="refunded" sort={sort} onSort={onSort} style={thStyle} />
                <SortTh label="Status"     col="status" sort={sort} onSort={onSort} style={thStyle} />
                <th style={{ padding: "12px 14px", fontSize: 11, fontWeight: 800,
                             whiteSpace: "nowrap", textAlign: "left", ...thStyle }}>Actions</th>
                <th style={{ ...thStyle, padding: "12px 10px", textAlign: "center" }}>
                  <input type="checkbox" checked={allExpanded}
                    onChange={e => setAllExpanded(e.target.checked)}
                    title={allExpanded ? "Collapse all" : "Expand all"}
                    style={{ cursor: "pointer", width: 14, height: 14, accentColor: "#f59e0b" }} />
                </th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: 40, textAlign: "center",
                                              color: T.textMuted, fontFamily: FONT }}>
                  No bookings found
                </td></tr>
              ) : paginated.map(b => (
                <BookingRow key={b.booking_id} b={b} T={T}
                  busy={busyIds[b.booking_id]}
                  forceExpand={allExpanded}
                  onAction={doAction}
                  onRefund={setRefundTarget} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
          {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
            p === page ? (
              <PrimaryButton key={p} size="sm" onClick={() => setPage(p)}
                style={{ width: 34, height: 34, padding: 0, borderRadius: 8, fontFamily: FONT }}>
                {p}
              </PrimaryButton>
            ) : (
              <GhostButton key={p} size="sm" onClick={() => setPage(p)}
                style={{ width: 34, height: 34, padding: 0, borderRadius: 8, fontFamily: FONT }}>
                {p}
              </GhostButton>
            )
          ))}
        </div>
      )}

      {refundTarget && (
        <RefundModal booking={refundTarget} T={T}
          onClose={() => setRefundTarget(null)}
          onDone={(amt) => handleRefundDone(refundTarget.booking_id, amt)} />
      )}
    </div>
  );
}
